/**
 * ReservationPanel.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Painel de uma reserva pendente na agenda (bloqueio "Reservado: ..."). Mostra
 * para quem é, o estado do link e as ações: lembrar o paciente do cadastro
 * (modelo de lembrete, com um link novo se o anterior venceu), copiar a mensagem
 * e liberar o horário.
 *
 * Liberar pode avisar o paciente: com número próprio a mensagem sai pelo
 * sistema (reservations/notify_released.php); sem ele abre o WhatsApp com a
 * mensagem pronta, no próprio clique para o navegador não bloquear a janela.
 *
 * @module ReservationPanel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import usePlanLock from '../../../../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../../consts/SubscriptionFeatures';
import { describeReservationSlot, formatReservationPhone, parseReservationDate } from '../../utils/reservationMessage';
import ReservationShare from './ReservationShare';
import ReleaseReservationDialog from './ReleaseReservationDialog';
import useReservationRelease from './useReservationRelease';
import { HourglassIcon } from './ReserveSlotDrawer';
import baseStyles from '../BlockSlotDrawer/BlockSlotDrawer.module.css';
import styles from './ReserveSlotDrawer.module.css';

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const LINK_STATUS = {
  active: 'Enviado, aguardando o cadastro',
  expired: 'Vencido: o lembrete leva um novo',
  used: 'Já usado: o lembrete leva um novo',
};

function formatCreatedAt(value) {
  if (!value) return '';
  const d = parseReservationDate(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} às ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen
 * @param {Object}   props.appointment - bloqueio da agenda (id, date)
 * @param {Function} props.onClose
 * @param {Function} props.onReleased - horário liberado (fecha e recarrega)
 */
export default function ReservationPanel({ isOpen, appointment, onClose, onReleased }) {
  const { GetAppointmentReservation, ResendAppointmentReservation } = DataProviderInstance();
  const planLock = usePlanLock(SUBSCRIPTION_PERMISSIONS.PATIENT_REGISTRATION_LINK, 'A reserva de horário com link de cadastro');

  const appointmentId = appointment?.id || appointment?.Id;
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  // Liberar horário (confirmação, aviso pelo WhatsApp e falha do aviso)
  const release = useReservationRelease({ appointmentId, data, onReleased });
  const { noticeFailed, openReleasedInWhatsApp } = release;
  const busy = isBusy || release.isBusy;

  useEffect(() => {
    if (!isOpen || !appointmentId) return;
    setData(null);
    setNotFound(false);
    setError('');
    setIsBusy(false);
    release.reset();
    GetAppointmentReservation(appointmentId, (res) => {
      if (res?.success) setData(res);
      else setNotFound(true);
    });
  }, [isOpen, appointmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = useCallback(() => {
    setError('');
    setIsBusy(true);
    ResendAppointmentReservation(appointmentId, (res) => {
      setIsBusy(false);
      if (!res?.success) {
        setError(res?.error || res?.message || 'Não foi possível enviar o lembrete. Tente novamente.');
        return;
      }
      setData(res);
    });
  }, [appointmentId, ResendAppointmentReservation]);

  const reservation = data?.reservation;

  if (!isOpen || !appointment) return null;

  return (
    <div className={baseStyles.drawerContainer} role="dialog" aria-modal="true" aria-label="Reserva de horário">
      <div className={baseStyles.drawerHeader}>
        <div className={baseStyles.drawerHeaderTop}>
          <h2 className={baseStyles.drawerTitle}>Reserva de horário</h2>
          <button className={baseStyles.drawerCloseBtn} onClick={noticeFailed ? () => onReleased?.() : onClose} aria-label="Fechar" title="Fechar" type="button">
            <XIcon />
          </button>
        </div>
        <div className={baseStyles.drawerHeaderDivider} />
        <div className={baseStyles.slotContext}>
          <div className={baseStyles.slotContextLeft}>
            <span className={baseStyles.slotContextIcon} aria-hidden="true"><HourglassIcon /></span>
            <div className={baseStyles.slotContextInfo}>
              <span className={baseStyles.slotDate}>{describeReservationSlot(appointment.date)}</span>
              <div className={baseStyles.slotTimeRow}>
                <span className={styles.pendingTag}>Aguardando cadastro</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={baseStyles.drawerBody}>
        {planLock.banner}

        {!data && !notFound && (
          <div className={styles.loading}><span className={baseStyles.spinner} /></div>
        )}

        {notFound && (
          <p className={styles.intro}>
            Esta reserva não está mais pendente: o paciente já se cadastrou ou o horário foi liberado.
            Atualize a agenda para ver a situação atual.
          </p>
        )}

        {error && <div className={baseStyles.validationError} role="alert">{error}</div>}

        {noticeFailed && (
          <div className={`${styles.statusBox} ${styles.statusWarning}`} role="alert">
            <span>
              Horário liberado, mas não foi possível avisar o paciente pelo WhatsApp da clínica.
              Envie o aviso pela conversa com ele.
            </span>
          </div>
        )}

        {reservation && !noticeFailed && (
          <>
            <dl className={styles.details}>
              <div className={styles.detailRow}>
                <dt>Telefone</dt>
                <dd>{formatReservationPhone(reservation.country_code, reservation.phone)}</dd>
              </div>
              {reservation.professional_name && (
                <div className={styles.detailRow}><dt>Agenda</dt><dd>{reservation.professional_name}</dd></div>
              )}
              {reservation.procedure_name && (
                <div className={styles.detailRow}>
                  <dt>{(reservation.procedures?.length || 0) > 1 ? 'Serviços' : 'Serviço'}</dt>
                  <dd>{reservation.procedure_name}</dd>
                </div>
              )}
              {reservation.room_name && (
                <div className={styles.detailRow}><dt>Sala</dt><dd>{reservation.room_name}</dd></div>
              )}
              <div className={styles.detailRow}><dt>Reservado em</dt><dd>{formatCreatedAt(reservation.created_at)}</dd></div>
              <div className={styles.detailRow}><dt>Link de cadastro</dt><dd>{LINK_STATUS[reservation.link_status]}</dd></div>
            </dl>

            <ReservationShare data={data} />
          </>
        )}
      </div>

      <div className={baseStyles.drawerFooterDivider} />
      <div className={baseStyles.drawerFooter}>
        {noticeFailed ? (
          <>
            <button type="button" className={baseStyles.btnCancel} onClick={() => onReleased?.()}>
              Fechar
            </button>
            <button type="button" className={styles.btnWhatsApp} onClick={openReleasedInWhatsApp}>
              Abrir no WhatsApp
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={baseStyles.btnCancel}
              onClick={release.openConfirm}
              disabled={busy}
            >
              Liberar horário
            </button>
            {reservation && (
              <button type="button" className={baseStyles.btnSave} onClick={planLock.guard(handleResend)} disabled={busy}>
                {busy && <span className={baseStyles.spinner} />}
                Enviar lembrete
              </button>
            )}
          </>
        )}
      </div>

      <ReleaseReservationDialog release={release} />

      {planLock.modal}
    </div>
  );
}
